import { useState } from 'react';
import { ArrowLeft, Upload, FileText, Calendar as CalendarIcon, Download, Eye, Trash2, CheckCircle, File, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';
import { PatientLayout } from './patient/PatientLayout';
import { LoadingState } from './common/LoadingState';
import { AlertCard } from './common/AlertCard';
import { useAuth } from '../lib/hooks/useAuth';
import { useMyLabResults, useUploadMyLabReport, useDeleteLabResult } from '../lib/hooks/useLabResults';
import { format } from 'date-fns';

interface LabReportUploadProps {
  onBack?: () => void;
  onNavigate?: (view: string) => void;
  onLogout?: () => void;
}

export function LabReportUpload({ onBack, onNavigate, onLogout }: LabReportUploadProps) {
  const { user, loading: authLoading } = useAuth();

  // API hooks - use new "me" endpoints for patients
  const { data: labResultsData, isLoading: labResultsLoading, error: labResultsError } = useMyLabResults();
  const uploadLabReport = useUploadMyLabReport();
  const deleteLabResult = useDeleteLabResult();

  // Form state
  const [reportType, setReportType] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [notes, setNotes] = useState('');

  const reportTypes = [
    'Blood Test',
    'Chest X-Ray',
    'CT Scan',
    'MRI Scan',
    'Spirometry Report',
    'ECG Report',
    'Ultrasound',
    'Biopsy Report',
    'Allergy Test',
    'Other',
  ];

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Invalid file type', {
          description: 'Please upload PDF, JPG, or PNG files only',
        });
        return;
      }

      // Validate file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large', {
          description: 'Maximum file size is 10MB',
        });
        return;
      }

      setSelectedFile(file);
      toast.success('File selected', {
        description: file.name,
      });
    }
  };

  const handleUpload = async () => {
    if (!reportType) {
      toast.error('Please select report type');
      return;
    }
    if (!reportDate) {
      toast.error('Please select report date');
      return;
    }
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      await uploadLabReport.mutateAsync({
        patient_id: 0,  // Not used by /me/upload endpoint
        test_type: reportType,
        test_date: reportDate,
        file: selectedFile,
        notes: notes || undefined,
      });

      // Reset form
      setReportType('');
      setReportDate('');
      setSelectedFile(null);
      setNotes('');
    } catch (error) {
      // Error already handled by the hook
    }
  };

  const getStatusBadge = (status: string) => {
    // ✅ FIX: Add null/undefined check
    if (!status) {
      return (
        <Badge variant="outline" className="bg-accent/10 text-accent border-accent">
          Pending Review
        </Badge>
      );
    }
    
    const normalizedStatus = status.toLowerCase();
    
    if (normalizedStatus === 'completed' || normalizedStatus === 'reviewed') {
      return (
        <Badge variant="outline" className="bg-[#14CAF4]/10 text-[#14CAF4] border-[#14CAF4]">
          <CheckCircle className="w-3 h-3 mr-1" />
          Reviewed
        </Badge>
      );
    }
    
    if (normalizedStatus === 'pending') {
      return (
        <Badge variant="outline" className="bg-accent/10 text-accent border-accent">
          Pending Review
        </Badge>
      );
    }
    
    return (
      <Badge variant="outline" className="bg-muted/10 text-muted-foreground border-muted">
        {status}
      </Badge>
    );
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleViewReport = (fileUrl?: string) => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    } else {
      toast.info('File preview not available');
    }
  };

  const handleDownloadReport = (fileUrl?: string, fileName?: string) => {
    if (fileUrl) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName || 'lab-report.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Download started');
    } else {
      toast.error('File not available for download');
    }
  };

  const handleDeleteReport = (id: number) => {
    deleteLabResult.mutate({ id });
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <PatientLayout 
        currentView="labreport" 
        onNavigate={onNavigate} 
        onLogout={onLogout}
        patientName={user?.full_name || user?.name || 'Patient'}
        adherenceScore={user?.adherence || 0}
      >
        <LoadingState message="Loading..." />
      </PatientLayout>
    );
  }

  // Show error if not a patient
  if (user && user.role !== 'PATIENT') {
    return (
      <PatientLayout 
        currentView="labreport" 
        onNavigate={onNavigate} 
        onLogout={onLogout}
        patientName={user?.full_name || user?.name || 'Patient'}
        adherenceScore={user?.adherence || 0}
      >
        <div className="max-w-7xl mx-auto px-4 py-6">
          <AlertCard
            type="error"
            title="Access Denied"
            description="This page is only accessible to patients."
            action={
              <Button onClick={onLogout} variant="outline">
                Logout
              </Button>
            }
          />
        </div>
      </PatientLayout>
    );
  }

  if (labResultsLoading) {
    return (
      <PatientLayout 
        currentView="labreport" 
        onNavigate={onNavigate} 
        onLogout={onLogout}
        patientName={user?.full_name || user?.name || 'Patient'}
        adherenceScore={user?.adherence || 0}
      >
        <LoadingState message="Loading lab reports..." />
      </PatientLayout>
    );
  }

  // ✅ FIX: Show better error message if patient profile is missing
  if (labResultsError) {
    const errorMessage = labResultsError?.message || String(labResultsError);
    const isProfileMissing = errorMessage.includes('not found') || errorMessage.includes('404');
    
    return (
      <PatientLayout 
        currentView="labreport" 
        onNavigate={onNavigate} 
        onLogout={onLogout}
        patientName={user?.full_name || user?.name || 'Patient'}
        adherenceScore={user?.adherence || 0}
      >
        <div className="max-w-7xl mx-auto px-4 py-6">
          <AlertCard
            type="error"
            title={isProfileMissing ? "Patient Profile Not Found" : "Unable to Load Lab Reports"}
            description={
              isProfileMissing
                ? "Your patient profile hasn't been set up yet. Please contact your care coordinator."
                : errorMessage
            }
            action={
              <div className="flex gap-2">
                <Button onClick={onBack} variant="outline">
                  Go Back
                </Button>
                <Button onClick={() => window.location.reload()}>
                  Retry
                </Button>
              </div>
            }
          />
        </div>
      </PatientLayout>
    );
  }

  const labResults = labResultsData || [];

  return (
    <PatientLayout 
      currentView="labreport"
      onNavigate={onNavigate}
      onLogout={onLogout}
      patientName={user?.full_name || user?.name || 'Patient'}
      adherenceScore={user?.adherence || 0}
    >
      <div className="min-h-screen bg-[#D4F0F8] pb-20">
        {/* Header */}
        <div className="bg-[#2762AC] text-white p-6 shadow-md">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-4">
              {onBack && (
                <Button
                  variant="ghost"
                  onClick={onBack}
                  className="text-white hover:bg-white/10 -ml-2"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              )}
              <div className="flex-1">
                <h1 className="text-primary-foreground">Lab Report Upload</h1>
                <p className="text-primary-foreground/80">Upload and manage your medical reports</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto p-6 space-y-6">
          {/* Upload New Report Card */}
          <Card className="shadow-lg">
            <CardHeader>
              <CardTitle className="text-primary flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload New Report
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Report Type */}
              <div className="space-y-2">
                <Label htmlFor="reportType" className="text-foreground">
                  Report Type *
                </Label>
                <Select value={reportType} onValueChange={setReportType}>
                  <SelectTrigger id="reportType" className="bg-background border-input">
                    <SelectValue placeholder="Select report type" />
                  </SelectTrigger>
                  <SelectContent>
                    {reportTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Report Date */}
              <div className="space-y-2">
                <Label htmlFor="reportDate" className="text-foreground">
                  Report Date *
                </Label>
                <div className="relative">
                  <Input
                    id="reportDate"
                    type="date"
                    value={reportDate}
                    onChange={(e) => setReportDate(e.target.value)}
                    max={format(new Date(), 'yyyy-MM-dd')}
                    className="bg-background border-input"
                  />
                  <CalendarIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
                </div>
              </div>

              {/* File Upload */}
              <div className="space-y-2">
                <Label htmlFor="fileUpload" className="text-foreground">
                  Upload File * (PDF, JPG, PNG - Max 10MB)
                </Label>
                <div className="relative">
                  <Input
                    id="fileUpload"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileChange}
                    className="bg-background border-input file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground hover:file:bg-primary/90"
                  />
                </div>
                {selectedFile && (
                  <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-md">
                    <File className="w-5 h-5 text-primary" />
                    <div className="flex-1">
                      <p className="text-sm">{selectedFile.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes (Optional) */}
              <div className="space-y-2">
                <Label htmlFor="notes" className="text-foreground">
                  Additional Notes (Optional)
                </Label>
                <Input
                  id="notes"
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional information about this report..."
                  className="bg-background border-input"
                />
              </div>

              {/* Upload Button */}
              <Button
                onClick={handleUpload}
                style={{ backgroundColor: '#2762AC' }}
                className="w-full text-white hover:opacity-90"
                disabled={!reportType || !reportDate || !selectedFile || uploadLabReport.isPending}
              >
                {uploadLabReport.isPending ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5 mr-2" />
                    Upload Report
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Previously Uploaded Reports */}
          <Card className="shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-primary flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Uploaded Reports
                </CardTitle>
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary">
                  {labResults.length} Reports
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {labResults.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-primary mb-2">No reports uploaded yet</h3>
                  <p className="text-muted-foreground">
                    Upload your first lab report using the form above
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {labResults.map((result) => (
                    <div
                      key={result.id}
                      className="border border-border rounded-lg p-4 bg-card hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 space-y-2">
                          {/* Report Type and Status */}
                          <div className="flex items-center gap-3 flex-wrap">
                            <h3 className="font-medium text-foreground">
                              {result.test_type || result.test_name}
                            </h3>
                            {getStatusBadge(result.status)}
                          </div>

                          {/* Report Details */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-muted-foreground">
                            <div className="flex items-center gap-2">
                              <CalendarIcon className="w-4 h-4" />
                              <span>Test Date: {format(new Date(result.test_date), 'MMM dd, yyyy')}</span>
                            </div>
                            {result.lab_name && (
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4" />
                                <span>Lab: {result.lab_name}</span>
                              </div>
                            )}
                            <div className="flex items-center gap-2">
                              <Upload className="w-4 h-4" />
                              <span>Uploaded: {format(new Date(result.created_at), 'MMM dd, yyyy')}</span>
                            </div>
                          </div>

                          {/* Notes */}
                          {result.notes && (
                            <p className="text-sm text-muted-foreground italic">
                              Notes: {result.notes}
                            </p>
                          )}

                          {/* Results (if available) */}
                          {result.result_value && (
                            <div className="flex items-center gap-2 text-sm">
                              <span className="text-muted-foreground">Result:</span>
                              <span className="font-medium">
                                {result.result_value} {result.result_unit}
                              </span>
                              {result.reference_range && (
                                <span className="text-muted-foreground">
                                  (Normal: {result.reference_range})
                                </span>
                              )}
                              {result.abnormal_flag && (
                                <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive">
                                  Abnormal
                                </Badge>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          {result.file_url && (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleViewReport(result.file_url)}
                                className="gap-2"
                              >
                                <Eye className="w-4 h-4" />
                                View
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDownloadReport(result.file_url, `${result.test_type}_${result.test_date}.pdf`)}
                                className="gap-2"
                              >
                                <Download className="w-4 h-4" />
                                Download
                              </Button>
                            </>
                          )}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeleteReport(result.id)}
                            className="gap-2"
                          >
                            <Trash2 className="w-4 h-4" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PatientLayout>
  );
}